'use client';

import React, {
  PropsWithChildren,
  useState,
  useContext,
  useEffect,
} from 'react';

export type DeviceTypeContextProps = {
  isMobile: boolean;
  isTablet: boolean;
  isXLScreen: boolean;
  screenWidth: number;
  isLargeScreen: boolean;
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
  xl2: boolean;
};

const detectDeviceByUA = () => {
  const ua = navigator?.userAgent || '';
  const uaDataMobile = (navigator as any)?.userAgentData?.mobile ?? false;

  const isUAMobile =
    /Android|iPhone|iPod|Mobile|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  const isUATablet = /iPad|Tablet|Nexus 7|Nexus 10|KFAPWI/i.test(ua);

  return {
    isUAMobile: isUAMobile || uaDataMobile,
    isUATablet,
  };
};

const defineDeviceType = () => {
  if (typeof window === 'undefined') {
    return {
      isMobile: true,
      isTablet: false,
      isXLScreen: false,
      isLargeScreen: false,
      screenWidth: 0,
      sm: false,
      md: false,
      lg: false,
      xl: false,
      xl2: false,
    };
  }

  const { isUAMobile, isUATablet } = detectDeviceByUA();
  const width = window.innerWidth;

  // SCREEN WIDTH CHECKS
  const widthMobile = width < 720;
  const widthTablet = width >= 720 && width < 1024;
  const widthXL = width >= 1024;
  const widthLargeScreen = width >= 1280;

  // FINAL DETECTION (combining screen size + UA)
  const isMobile = widthMobile || isUAMobile;
  const isTablet = widthTablet || isUATablet;
  const isXLScreen = widthXL;
  const isLargeScreen = widthLargeScreen;
  const sm = width >= 640;
  const md = width >= 768;
  const lg = width >= 1024;
  const xl = width >= 1280;
  const xl2 = width >= 1536;

  return {
    isMobile,
    isTablet,
    isXLScreen,
    isLargeScreen,
    screenWidth: width,
    sm,
    md,
    lg,
    xl,
    xl2,
  };
};

const DeviceTypeContext = React.createContext<DeviceTypeContextProps>({
  isMobile: true,
  isTablet: false,
  isXLScreen: false,
  isLargeScreen: false,
  screenWidth: 0,
  sm: true,
  md: false,
  lg: false,
  xl: false,
  xl2: false,
});

/**
 * User provider.
 */
export const DeviceTypeProvider = ({ children }: PropsWithChildren) => {
  const initial = defineDeviceType();
  const [isMobile, setIsMobile] = useState(initial.isMobile);
  const [isTablet, setIsTablet] = useState(initial.isTablet);
  const [isXLScreen, setIsXLScreen] = useState(initial.isXLScreen);
  const [isLargeScreen, setIsLargeScreen] = useState(initial.isLargeScreen);
  const [screenWidth, setScreenWidth] = useState(initial.screenWidth);
  const [sm, setSm] = useState(initial.sm);
  const [md, setMd] = useState(initial.md);
  const [lg, setLg] = useState(initial.lg);
  const [xl, setXl] = useState(initial.xl);
  const [xl2, setXl2] = useState(initial.xl2);

  useEffect(() => {
    const handleResize = () => {
      const {
        isMobile,
        isTablet,
        isXLScreen,
        isLargeScreen,
        screenWidth,
        sm,
        md,
        lg,
        xl,
        xl2,
      } = defineDeviceType();
      setIsMobile(isMobile);
      setIsTablet(isTablet);
      setIsXLScreen(isXLScreen);
      setIsLargeScreen(isLargeScreen);
      setScreenWidth(screenWidth);
      setSm(sm);
      setMd(md);
      setLg(lg);
      setXl(xl);
      setXl2(xl2);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <DeviceTypeContext.Provider
      value={{
        isMobile,
        isTablet,
        isXLScreen,
        isLargeScreen,
        screenWidth,
        sm,
        md,
        lg,
        xl,
        xl2,
      }}
    >
      {children}
    </DeviceTypeContext.Provider>
  );
};

/**
 * Hook to access the user context.
 */
export const useDeviceType = (): DeviceTypeContextProps =>
  useContext(DeviceTypeContext);
